import React, { useState, useEffect, useRef } from "react";
import { useApp } from "../../contexts/AppContext";
import { apiService } from "../../services/api";
import { Event as MallEvent, RouteEntity } from "../../types";
import styles from "./TabContent.module.css";
import { useLocation } from "react-router-dom";
import { getSocialMediaIcon, formatSocialMediaUrl } from "./socialMediaUtils";

interface EventTabContentProps {
  activeTabId: string | null;
  tabName: string;
  onTabChange?: (tabId: string | null) => void;
  tabIdNumber: number | null;
}

export default function EventTabContent({
  activeTabId,
  tabName,
  onTabChange,
  tabIdNumber,
}: EventTabContentProps) {
  const {
    state,
    setSelectedShop,
    setCurrentFloor,
    setActiveRoutePoints,
    setSelectedShopReload,
    setTerminalName,
    setCurrentFloorReload,
    setSelectedEvent,
  } = useApp();
  const [events, setEvents] = useState<MallEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const selectedEvent = state.selectedEvent || null;
  const [currentEventPhotoIndex, setCurrentEventPhotoIndex] = useState(0);
  const routerLocation = useLocation();
  const eventThumbnailsRef = useRef<HTMLDivElement>(null);

  // Reset selected event when tab changes
  useEffect(() => {
    setSelectedEvent(undefined);
    setCurrentEventPhotoIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId]);

  // When an event is selected, also select its linked shop (for map highlighting/routes)
  useEffect(() => {
    const applyLinkedShopSelection = async () => {
      if (!selectedEvent || !selectedEvent.shopId) {
        return;
      }
      try {
        const shop = await apiService.getShopById(String(selectedEvent.shopId));
        setSelectedShop(shop);
      } catch (e) {
        // ignore if shop fetch fails
      }
    };
    applyLinkedShopSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent?.shopId]);

  // Load events
  useEffect(() => {
    if (!activeTabId) return;
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId, state.language]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const allEvents = await apiService.getEvents({ active: true });
      setEvents(allEvents);
    } catch (error) {
      console.error("Error loading events:", error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle horizontal scroll with mouse wheel for event thumbnails
  useEffect(() => {
    const container = eventThumbnailsRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      if (container.scrollWidth > container.clientWidth) {
        e.preventDefault();
        container.scrollLeft += e.deltaY;
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [selectedEvent]);

  // Handle go to catalog (back to grid)
  const handleGoToCatalog = () => {
    setSelectedEvent(undefined);
    setCurrentEventPhotoIndex(0);
  };

  // Handle go to map
  const handleGoToMap = () => {
    const mapTab = state.tabs?.find((tab) => parseInt(tab.id) === 1);
    if (mapTab && onTabChange) {
      onTabChange(mapTab.id);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className={styles.contentContainer}>
        <div className={styles.loadingState}>
          {state.language === "en" ? "Loading..." : "Загрузка..."}
        </div>
      </div>
    );
  }

  // Show event detail if an event is selected
  if (selectedEvent) {
    const photos = selectedEvent.photos || [];
    const currentPhoto =
      photos.length > 0 ? photos[currentEventPhotoIndex] : null;
    const eventName =
      state.language === "en"
        ? selectedEvent.nameEn || selectedEvent.nameRu
        : selectedEvent.nameRu;
    const fullDescription =
      state.language === "en"
        ? selectedEvent.fullDescriptionEn || selectedEvent.fullDescriptionRu
        : selectedEvent.fullDescriptionRu;
    const location =
      state.language === "en"
        ? selectedEvent.locationEn || selectedEvent.locationRu
        : selectedEvent.locationRu;
    const workingHours =
      state.language === "en"
        ? selectedEvent.workingHoursEn || selectedEvent.workingHoursRu
        : selectedEvent.workingHoursRu;

    return (
      <div className={styles.contentContainer}>
        <div className={styles.shopDetailContainer}>
          <div className={styles.shopDetailTop}>
            <div className={styles.shopHeaderInfo}>
              <div className={styles.infoRow}>
                <div>
                  {selectedEvent.shop?.logoUrl && (
                    <img
                      className={styles.shopDetailLogo}
                      src={`${process.env.REACT_APP_MEDIA_PATH}${selectedEvent.shop.logoUrl}`}
                      alt={eventName}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect width='120' height='120' fill='%23f0f0f0'/%3E%3C/svg%3E";
                      }}
                    />
                  )}
                </div>

                {location && (
                  <div className={styles.infoItemRow}>
                    <span className={styles.infoValue}>{location}</span>
                  </div>
                )}

                {workingHours && (
                  <div className={styles.infoItem}>
                    <span className={styles.infoWorkingHours}>
                      {workingHours}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={styles.shopDetailRow}>
            <div className={styles.shopDetailLeft}>
              <h1 className={styles.shopDetailName}>{eventName}</h1>

              {fullDescription && (
                <p className={styles.shopDetailDescription}>
                  {fullDescription}
                </p>
              )}

              <div className={styles.actionButtons}>
                <button
                  className={`${styles.actionButton} ${styles.actionButtonPrimary}`}
                  onClick={async () => {
                    if (!selectedEvent || !selectedEvent.shopId) return;
                    const params = new URLSearchParams(routerLocation.search);
                    const terminalName = params.get("terminalName");
                    try {
                      const fullShop = await apiService.getShopById(
                        String(selectedEvent.shopId)
                      );
                      setSelectedShop(fullShop);

                      const terminals = await apiService.getTerminals({
                        active: true,
                      });
                      const terminal = terminalName
                        ? terminals.find((t) => t.name === terminalName)
                        : undefined;
                      if (!terminal) {
                        setActiveRoutePoints(undefined);
                        return;
                      }
                      const routes: RouteEntity[] =
                        await apiService.getRoutes();
                      const match = routes.find(
                        (r) =>
                          Number(r.shopId) === Number(selectedEvent.shopId) &&
                          Number(r.terminalId) === Number(terminal.id)
                      );
                      if (match && match.points && match.points.length > 0) {
                        setSelectedShopReload(fullShop);
                        setTerminalName(terminalName || undefined);
                        setCurrentFloorReload(terminal.floor);
                        if (state.currentFloor !== terminal.floor) {
                          setCurrentFloor(terminal.floor);
                        }
                        setTimeout(() => {
                          setActiveRoutePoints(match.points);
                        }, 50);
                        const mapTab = state.tabs?.find(
                          (tab) => parseInt(tab.id) === 1
                        );
                        if (mapTab && onTabChange) {
                          onTabChange(mapTab.id);
                        }
                      } else {
                        setActiveRoutePoints(undefined);
                      }
                    } catch (e) {
                      console.error("Error building route:", e);
                      setActiveRoutePoints(undefined);
                    }
                  }}
                >
                  {state.language === "en"
                    ? "Build Route"
                    : "Построить маршрут"}
                </button>
                <button
                  className={styles.actionButton}
                  onClick={handleGoToCatalog}
                >
                  {state.language === "en"
                    ? "Go to Catalog"
                    : "Перейти в каталог"}
                </button>
                <button className={styles.actionButton} onClick={handleGoToMap}>
                  {state.language === "en" ? "Go to Map" : "Перейти на карту"}
                </button>
              </div>
            </div>
            <div className={styles.shopDetailRight}>
              {photos.length > 0 && currentPhoto && (
                <div className={styles.imageSlider}>
                  <div className={styles.imageSliderContainer}>
                    {photos.length > 1 && (
                      <button
                        className={styles.sliderButton}
                        onClick={() => {
                          setCurrentEventPhotoIndex(
                            currentEventPhotoIndex > 0
                              ? currentEventPhotoIndex - 1
                              : photos.length - 1
                          );
                        }}
                        aria-label={
                          state.language === "en"
                            ? "Previous photo"
                            : "Предыдущее фото"
                        }
                      >
                        ‹
                      </button>
                    )}
                    <img
                      className={styles.mainImage}
                      src={`${process.env.REACT_APP_MEDIA_PATH}${currentPhoto.url}`}
                      alt={eventName}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "data:image/svg+xml,%3Csvg width='348' height='480' viewBox='0 0 348 480' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='348' height='480' fill='%23FFFAF3'/%3E%3Cpath d='M102.54 161.46C102.075 160.996 101.523 160.627 100.916 160.375C100.308 160.124 99.6574 159.994 99 159.994C98.3425 159.994 97.6915 160.124 97.0841 160.375C96.4767 160.627 95.9248 160.996 95.46 161.46C94.9951 161.925 94.6263 162.477 94.3747 163.085C94.1231 163.692 93.9937 164.343 93.9937 165C93.9937 165.658 94.1231 166.309 94.3747 166.916C94.6263 167.524 94.9951 168.076 95.46 168.54L109.55 182.62C105.928 187.691 103.987 193.769 104 200V280C104 287.957 107.161 295.588 112.787 301.214C118.413 306.84 126.043 310 134 310H214C220.231 310.013 226.309 308.072 231.38 304.45L245.46 318.54C246.399 319.479 247.672 320.007 249 320.007C250.328 320.007 251.601 319.479 252.54 318.54C253.479 317.602 254.006 316.328 254.006 315C254.006 313.673 253.479 312.399 252.54 311.46L102.54 161.46ZM168.14 241.21C166.409 241.946 164.831 243.001 163.49 244.32L116.79 290.2C114.962 287.112 113.998 283.589 114 280V200C114 196.3 115.01 192.82 116.77 189.84L168.14 241.21ZM134 300C130.3 300 126.85 299 123.88 297.26L170.5 251.46C171.435 250.544 172.691 250.031 174 250.031C175.309 250.031 176.565 250.544 177.5 251.46L224.12 297.26C221.05 299.057 217.557 300.003 214 300H134ZM234 200V278.79L242.98 287.77C243.66 285.284 244 282.694 244 280V200C244 192.044 240.839 184.413 235.213 178.787C229.587 173.161 221.956 170 214 170H134C131.3 170 128.71 170.34 126.23 171.02L135.2 180H214C219.304 180 224.391 182.108 228.142 185.858C231.893 189.609 234 194.696 234 200ZM214 215C214 213.031 213.612 211.08 212.858 209.26C212.104 207.44 210.999 205.787 209.607 204.394C208.214 203.001 206.56 201.896 204.74 201.142C202.92 200.388 200.97 200 199 200C197.03 200 195.08 200.388 193.26 201.142C191.44 201.896 189.786 203.001 188.393 204.394C187 205.787 185.896 207.44 185.142 209.26C184.388 211.08 184 213.031 184 215C184 218.979 185.58 222.794 188.393 225.607C191.206 228.42 195.022 230 199 230C202.978 230 206.794 228.42 209.607 225.607C212.42 222.794 214 218.979 214 215ZM204 215C204 216.327 203.473 217.598 202.536 218.536C201.598 219.474 200.326 220 199 220C197.674 220 196.402 219.474 195.464 218.536C194.527 217.598 194 216.327 194 215C194 213.674 194.527 212.403 195.464 211.465C196.402 210.527 197.674 210 199 210C200.326 210 201.598 210.527 202.536 211.465C203.473 212.403 204 213.674 204 215Z' fill='url(%23paint0_linear_5310_812)'/%3E%3Cdefs%3E%3ClinearGradient id='paint0_linear_5310_812' x1='174' y1='159.994' x2='174' y2='320.007' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%2364A696'/%3E%3Cstop offset='0.5' stop-color='%2364A696'/%3E%3Cstop offset='1' stop-color='%2364A696'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E";
                      }}
                    />
                    {photos.length > 1 && (
                      <button
                        className={styles.sliderButton}
                        onClick={() => {
                          setCurrentEventPhotoIndex(
                            currentEventPhotoIndex < photos.length - 1
                              ? currentEventPhotoIndex + 1
                              : 0
                          );
                        }}
                        aria-label={
                          state.language === "en"
                            ? "Next photo"
                            : "Следующее фото"
                        }
                      >
                        ›
                      </button>
                    )}
                  </div>
                  {photos.length > 1 && (
                    <div className={styles.imageSliderInfo}>
                      <span className={styles.imageSliderCounter}>
                        {currentEventPhotoIndex + 1} / {photos.length}
                      </span>
                    </div>
                  )}

                  {photos.length > 1 && (
                    <div
                      className={styles.imageThumbnails}
                      ref={eventThumbnailsRef}
                    >
                      {photos.map((photo, index) => (
                        <img
                          key={photo.id}
                          className={`${styles.thumbnail} ${
                            index === currentEventPhotoIndex
                              ? styles.thumbnailActive
                              : ""
                          }`}
                          src={`${process.env.REACT_APP_MEDIA_PATH}${photo.url}`}
                          alt={`${eventName} - Photo ${index + 1}`}
                          onClick={() => setCurrentEventPhotoIndex(index)}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Crect width='80' height='80' fill='%23f0f0f0'/%3E%3C/svg%3E";
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {selectedEvent.socialMedia && (
          <div className={styles.socialMedia}>
            {Object.entries(selectedEvent.socialMedia)
              .filter(([, url]) => url && typeof url === "string")
              .map(([platform, url]) => {
                if (!url || typeof url !== "string") return null;
                return (
                  <a key={platform} className={styles.socialIcon}>
                    {getSocialMediaIcon(platform, selectedEvent.id)}
                    {formatSocialMediaUrl(url)}
                  </a>
                );
              })}
          </div>
        )}
      </div>
    );
  }

  // Show Events grid
  return (
    <div className={styles.contentContainer}>
      <div className={styles.headerSection}>
        <h2 className={styles.contentTitle}>{tabName}</h2>
      </div>

      {events.length === 0 ? (
        <div className={styles.emptyState}>
          {state.language === "en" ? "No events found" : "События не найдены"}
        </div>
      ) : (
        <div className={styles.shopsGrid}>
          {events.map((eventItem) => (
            <div
              key={eventItem.id}
              className={styles.eventCard}
              onClick={async () => {
                // Загружаем полную информацию о событии с фотографиями
                try {
                  const fullEvent = await apiService.getEventById(eventItem.id);
                  setSelectedEvent(fullEvent);
                  setCurrentEventPhotoIndex(0);
                } catch (e) {
                  console.error("Error loading event:", e);
                }
              }}
            >
              {eventItem.logoUrl ? (
                <img
                  className={styles.eventShopLogo}
                  src={`${process.env.REACT_APP_MEDIA_PATH}${eventItem.logoUrl}`}
                  alt={
                    state.language === "en"
                      ? eventItem.nameEn || eventItem.nameRu
                      : eventItem.nameRu
                  }
                  // onError={(e) => {
                  //   (e.target as HTMLImageElement).src =
                  //     "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%23f0f0f0'/%3E%3C/svg%3E";
                  // }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "data:image/svg+xml,%3Csvg width='348' height='480' viewBox='0 0 348 480' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='348' height='480' fill='%23FFFAF3'/%3E%3Cpath d='M102.54 161.46C102.075 160.996 101.523 160.627 100.916 160.375C100.308 160.124 99.6574 159.994 99 159.994C98.3425 159.994 97.6915 160.124 97.0841 160.375C96.4767 160.627 95.9248 160.996 95.46 161.46C94.9951 161.925 94.6263 162.477 94.3747 163.085C94.1231 163.692 93.9937 164.343 93.9937 165C93.9937 165.658 94.1231 166.309 94.3747 166.916C94.6263 167.524 94.9951 168.076 95.46 168.54L109.55 182.62C105.928 187.691 103.987 193.769 104 200V280C104 287.957 107.161 295.588 112.787 301.214C118.413 306.84 126.043 310 134 310H214C220.231 310.013 226.309 308.072 231.38 304.45L245.46 318.54C246.399 319.479 247.672 320.007 249 320.007C250.328 320.007 251.601 319.479 252.54 318.54C253.479 317.602 254.006 316.328 254.006 315C254.006 313.673 253.479 312.399 252.54 311.46L102.54 161.46ZM168.14 241.21C166.409 241.946 164.831 243.001 163.49 244.32L116.79 290.2C114.962 287.112 113.998 283.589 114 280V200C114 196.3 115.01 192.82 116.77 189.84L168.14 241.21ZM134 300C130.3 300 126.85 299 123.88 297.26L170.5 251.46C171.435 250.544 172.691 250.031 174 250.031C175.309 250.031 176.565 250.544 177.5 251.46L224.12 297.26C221.05 299.057 217.557 300.003 214 300H134ZM234 200V278.79L242.98 287.77C243.66 285.284 244 282.694 244 280V200C244 192.044 240.839 184.413 235.213 178.787C229.587 173.161 221.956 170 214 170H134C131.3 170 128.71 170.34 126.23 171.02L135.2 180H214C219.304 180 224.391 182.108 228.142 185.858C231.893 189.609 234 194.696 234 200ZM214 215C214 213.031 213.612 211.08 212.858 209.26C212.104 207.44 210.999 205.787 209.607 204.394C208.214 203.001 206.56 201.896 204.74 201.142C202.92 200.388 200.97 200 199 200C197.03 200 195.08 200.388 193.26 201.142C191.44 201.896 189.786 203.001 188.393 204.394C187 205.787 185.896 207.44 185.142 209.26C184.388 211.08 184 213.031 184 215C184 218.979 185.58 222.794 188.393 225.607C191.206 228.42 195.022 230 199 230C202.978 230 206.794 228.42 209.607 225.607C212.42 222.794 214 218.979 214 215ZM204 215C204 216.327 203.473 217.598 202.536 218.536C201.598 219.474 200.326 220 199 220C197.674 220 196.402 219.474 195.464 218.536C194.527 217.598 194 216.327 194 215C194 213.674 194.527 212.403 195.464 211.465C196.402 210.527 197.674 210 199 210C200.326 210 201.598 210.527 202.536 211.465C203.473 212.403 204 213.674 204 215Z' fill='url(%23paint0_linear_5310_812)'/%3E%3Cdefs%3E%3ClinearGradient id='paint0_linear_5310_812' x1='174' y1='159.994' x2='174' y2='320.007' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%2364A696'/%3E%3Cstop offset='0.5' stop-color='%2364A696'/%3E%3Cstop offset='1' stop-color='%2364A696'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E";
                  }}
                />
              ) : (
                <img
                  className={styles.eventShopLogo}
                  src="data:image/svg+xml,%3Csvg width='348' height='480' viewBox='0 0 348 480' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='348' height='480' fill='%23FFFAF3'/%3E%3Cpath d='M102.54 161.46C102.075 160.996 101.523 160.627 100.916 160.375C100.308 160.124 99.6574 159.994 99 159.994C98.3425 159.994 97.6915 160.124 97.0841 160.375C96.4767 160.627 95.9248 160.996 95.46 161.46C94.9951 161.925 94.6263 162.477 94.3747 163.085C94.1231 163.692 93.9937 164.343 93.9937 165C93.9937 165.658 94.1231 166.309 94.3747 166.916C94.6263 167.524 94.9951 168.076 95.46 168.54L109.55 182.62C105.928 187.691 103.987 193.769 104 200V280C104 287.957 107.161 295.588 112.787 301.214C118.413 306.84 126.043 310 134 310H214C220.231 310.013 226.309 308.072 231.38 304.45L245.46 318.54C246.399 319.479 247.672 320.007 249 320.007C250.328 320.007 251.601 319.479 252.54 318.54C253.479 317.602 254.006 316.328 254.006 315C254.006 313.673 253.479 312.399 252.54 311.46L102.54 161.46ZM168.14 241.21C166.409 241.946 164.831 243.001 163.49 244.32L116.79 290.2C114.962 287.112 113.998 283.589 114 280V200C114 196.3 115.01 192.82 116.77 189.84L168.14 241.21ZM134 300C130.3 300 126.85 299 123.88 297.26L170.5 251.46C171.435 250.544 172.691 250.031 174 250.031C175.309 250.031 176.565 250.544 177.5 251.46L224.12 297.26C221.05 299.057 217.557 300.003 214 300H134ZM234 200V278.79L242.98 287.77C243.66 285.284 244 282.694 244 280V200C244 192.044 240.839 184.413 235.213 178.787C229.587 173.161 221.956 170 214 170H134C131.3 170 128.71 170.34 126.23 171.02L135.2 180H214C219.304 180 224.391 182.108 228.142 185.858C231.893 189.609 234 194.696 234 200ZM214 215C214 213.031 213.612 211.08 212.858 209.26C212.104 207.44 210.999 205.787 209.607 204.394C208.214 203.001 206.56 201.896 204.74 201.142C202.92 200.388 200.97 200 199 200C197.03 200 195.08 200.388 193.26 201.142C191.44 201.896 189.786 203.001 188.393 204.394C187 205.787 185.896 207.44 185.142 209.26C184.388 211.08 184 213.031 184 215C184 218.979 185.58 222.794 188.393 225.607C191.206 228.42 195.022 230 199 230C202.978 230 206.794 228.42 209.607 225.607C212.42 222.794 214 218.979 214 215ZM204 215C204 216.327 203.473 217.598 202.536 218.536C201.598 219.474 200.326 220 199 220C197.674 220 196.402 219.474 195.464 218.536C194.527 217.598 194 216.327 194 215C194 213.674 194.527 212.403 195.464 211.465C196.402 210.527 197.674 210 199 210C200.326 210 201.598 210.527 202.536 211.465C203.473 212.403 204 213.674 204 215Z' fill='url(%23paint0_linear_5310_812)'/%3E%3Cdefs%3E%3ClinearGradient id='paint0_linear_5310_812' x1='174' y1='159.994' x2='174' y2='320.007' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%2364A696'/%3E%3Cstop offset='0.5' stop-color='%2364A696'/%3E%3Cstop offset='1' stop-color='%2364A696'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E"
                  alt="No logo"
                />
              )}
              <div className={styles.eventShopName}>
                {state.language === "en"
                  ? eventItem.nameEn || eventItem.nameRu
                  : eventItem.nameRu}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
